import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;
    return {
        shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
        totalCountDisplay: cart?.totalCountDisplay,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        isCartCheckoutDisabled: isCartLoading || !cart || isCartCheckoutDisabled(cart) || isUpdatingCart,
        cart,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    preloadCheckoutShippingData,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    desktopCartText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    countGridItem?: GridItemProps;
    totalGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    checkoutButton?: ButtonPresentationProps;
    buttonsWrapper?: InjectableCss;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
            size: 12,
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "black",
        css: css`
            height: 28.75px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        &:hover span:last-child > div {
                            display: flex;
                            position: absolute;
                            transform: translate(-280px, 0px);
                        }
                    `,
                    css`
                        &:hover span:last-child > div {
                            display: flex;
                            position: absolute;
                            transform: translate(-200px, 0px);
                        }
                    `,
                ])}
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
    desktopCountText: {
        css: css`
            font-weight: normal;
            display: block;
        `,
    },
    desktopCartText: {
        css: css`
            font-weight: 800;
            text-align: left;
            display: block;
        `,
    },
    container: {
        gap: 0,
        css: css`
            box-shadow: 0px 0px 4px 2px rgb(0 0 0 / 20%);
            margin: 0px 5px 0px 0px;
            justify-content: center;
            width: 360px;
            min-height:100px;
            background: white;
            display:none;
            z-index:1;
            tabindex="-1"
        `,
        offsetCss: css`
            justify-content: space-evenly;
        `,
    },
    labelGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    countGridItem: {
        width: 12,
        css: css`
            color: white;
            background-color: black;
            justify-content: center;
            padding: 14px;
            font-weight: bold;
        `,
    },
    totalGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            padding: 14px 14px 6px 14px;
            font-weight: bold;
        `,
    },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            tabindex="-1"
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: space-evenly;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        position: fixed;
                        left: 0;
                        bottom: 0;
                        width: 100%;
                        background: ${getColor("common.background")({ theme })};
                        justify-content: center;
                        z-index: ${get(theme, "zIndex.stickyFooter")};
                    `,
                ])}
        `,
    },
    checkoutButton: {
        css: css`
            color: black;
            margin: 10px;
            flex-shrink: 0;
        `,
    },
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const styles = cartLinkStyles;
const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    cart,
    history,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
    theme,
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    useEffect(() => {
        if (typeof retainedTotalCountToDisplay !== "undefined" && retainedTotalCountToDisplay === 0) {
            routerLink.css = css`
                height: 28.75px;
            `;
        }
        if (typeof retainedTotalCountToDisplay !== "undefined" && retainedTotalCountToDisplay > 0) {
            const { innerWidth, innerHeight } = window;
            if (innerWidth < 992) {
                routerLink.css = css`
                    height: 28.75px;
                    padding: 0;
                `;
            } else if (innerWidth >= 992) {
                routerLink.css = css`
                    &:hover span:last-child > div {
                        padding: 0;
                        display: flex;
                        position: absolute;
                        transform: translate(-280px, 0px);
                    }
                `;
            }
        }
    }, [retainedTotalCountToDisplay]);

    const cartHandler = () => {
        history.push(cartUrl!);
    };

    const continueClickHandler = () => {
        history.push("/ShopAll");
    };

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    if (typeof retainedTotalCountToDisplay !== "undefined" && retainedTotalCountToDisplay === 0) {
        routerLink.css = css`
            height: 28.75px;
        `;
    }
    if (typeof retainedTotalCountToDisplay !== "undefined" && retainedTotalCountToDisplay > 0) {
        const { innerWidth, innerHeight } = window;
        if (innerWidth < 992) {
            routerLink.css = css`
                height: 28.75px;
            `;
        } else if (innerWidth >= 992) {
            routerLink.css = css`
                &:hover span:last-child > div {
                    display: flex;
                    position: absolute;
                    transform: translate(-280px, 0px);
                }
            `;
        }
    }

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    return (
        <Link
            {...routerLink}
            href={cartUrl}
            icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
            data-test-selector="cartLink"
        >
            <Hidden below="lg" as="span">
                <Typography {...styles.desktopCartText}>{showLabel && `${translate("Cart")} `}</Typography>
                <Typography as="span" {...styles.desktopCountText} data-test-selector="cartLinkQuantity">
                    {retainedTotalCountToDisplay}{" "}
                    {retainedTotalCountToDisplay && retainedTotalCountToDisplay === 1
                        ? translate("item")
                        : translate("items")}
                </Typography>
            </Hidden>
            <Hidden above="md">
                <Typography as="span" {...styles.mobileCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
            </Hidden>
            <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            <GridContainer {...styles.container}>
                <GridItem {...styles.countGridItem}>
                    <Typography as="span" {...styles.desktopCountText}>
                        {retainedTotalCountToDisplay}{" "}
                        {retainedTotalCountToDisplay && retainedTotalCountToDisplay > 1
                            ? translate("items in your cart")
                            : translate("item in your cart")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.totalGridItem}>
                    <Typography as="span" {...styles.desktopCountText}>
                        {translate("Subtotal:")} {cart?.orderSubTotalDisplay}
                    </Typography>
                </GridItem>
                <GridItem {...styles.buttonGridItem}>
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button
                            {...styles.checkoutButton}
                            onClick={continueClickHandler}
                            disabled={isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts}
                            data-test-selector="miniCart_continue"
                        >
                            {translate("Continue Shopping")}
                        </Button>
                        <Button
                            {...styles.checkoutButton}
                            onClick={cartHandler}
                            disabled={isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts}
                            data-test-selector="miniCart_cart"
                        >
                            {cart?.requiresApproval ? translate("Checkout for Approval") : translate("Checkout")}
                        </Button>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </Link>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(CartLink))),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
